import { useEffect, useId, useRef, useState } from "react";

/**
 * The confirmation a family answers before a write lands — **and the panel's most constrained
 * component** (ADR 0021 decision 8, issue #302).
 *
 * ## It renders the server's words and composes none of its own
 *
 * This is the named control the ADR says it will be revised over, so it is worth stating
 * plainly rather than leaving to the test: **every sentence in this dialog arrives from the
 * server.** The question is the tool's own `message`. The lines beneath it are the tool's own
 * `doesNotReach`. The panel's entire vocabulary here is two button labels and a failure line,
 * and none of them says anything about *what is being written*.
 *
 * The reason is ADR 0014 decision 1. A confirmation proves the family's authenticated session
 * asked for one exact stored write — not that a person was shown the server's message rather
 * than a paraphrase of it. In a chat host that gap is unavoidable, because the host owns the
 * conversation. **Here we own the surface, so the gap is ours to close and closing it is free.**
 * A panel that summarised the write in its own words would re-create, on the one surface that
 * did not have to have it, exactly the weakness that decision records.
 *
 * It follows that a disclosure the family needs has to reach them *through* the server's
 * strings. That is already true and is why this works: `forget_this` builds the #241
 * *"N other things you asked me to remember had the very same words"* clause into its
 * `message`, so rendering the message verbatim discloses the count — the panel does not have
 * to assemble that sentence from `deleting.quotedOnOtherFacts`, and must not.
 *
 * ## Two things it deliberately does not do
 *
 * **It has no heading of its own.** A heading would be panel copy describing the write, so the
 * dialog is labelled by the server's message instead (`aria-labelledby`) — which is also the
 * better accessible name, since it is what a screen reader should read first.
 *
 * **A click here is not a stronger confirmation than a host's `confirm: true`.** ADR 0014
 * decision 1 is unchanged by this surface existing: the tier, the stored proposal and the
 * `expectedTool` check are what make the write safe, and this dialog is how a family is asked.
 *
 * ## The one string that was added, and why it is not a third voice (#303)
 *
 * {@link gate} makes the confirm button inert until somebody types a value back — the family's
 * own name, in front of `disband_family`. It carries one sentence of panel copy (*"Type the
 * family's name to confirm"*), and the rule above survives it because **that sentence says
 * nothing about the write**: it is an instruction for a control, of exactly the kind *Cancel*
 * already is. What is being deleted, how much of it, and what deletion cannot reach are still
 * the server's `message` and `doesNotReach`, unedited.
 *
 * The ceremony is here rather than in a dialog of the roster's own because a second dialog
 * would be a second place a write could be described, which is the thing this component
 * exists to prevent. It is **not** a security control and does not pretend to be: the typed
 * name stops a mis-click, and {@link ConfirmChallenge} is what stands between a walked-away
 * laptop and four people's data.
 *
 * ## The second factor, and why its screen is not the gate (#307)
 *
 * {@link challenge} swaps the dialog's body for a code entry when the server has refused a
 * covered write with `step_up_required` — the writes that end a household, end an account, or
 * hand away the actor's own authority (ADR 0021 decision 8a).
 *
 * **Every word of it is an instruction for a control**, which is the licence the gate's label
 * already runs on: *Enter the code*, *Didn't arrive?*, *Send another* say nothing whatsoever
 * about the write. The one piece of content is the **address**, and that is the server's —
 * `POST /app/me/step-up` resolves it from the session and answers with it, so the panel does
 * not know, guess or compose it. The rule survives intact.
 *
 * **The server's `message` is not rendered on this step**, matching `DisbandFlow.dc.html`,
 * and that is a deliberate exception rather than an erosion. The question has already been
 * asked and answered on the step before; this step asks a different one — *are you the person
 * whose account this is* — and repeating the write's description underneath a code field would
 * make it look as though typing the code were answering the write again. The dialog's
 * accessible name follows the body, so on this step it is the challenge's own heading.
 *
 * **And the screen is not the control.** A client that never rendered this, or skipped
 * straight to the confirm, is refused by the route: `services/api/src/app/writes.ts` verifies
 * the factor with Stytch before the tool is invoked. This is how a family is *asked*, exactly
 * as the dialog itself is how a family is asked to confirm — neither is what makes the write
 * safe.
 *
 * ## The second write class arrives, and the rule survives it (#116)
 *
 * ADR 0021 decision 5 has always named two kinds of write, and until #116 only one of them
 * had a screen. Cutting off a connected app is the other: no `PROPOSAL#`, because a grant is
 * held by a *person* and a proposal lives in a `FAMILY#` partition. It still needs a
 * confirmation, and it is confirmed **here** rather than in a dialog of its own, for the same
 * reason `disband_family`'s ceremony is here — a second dialog is a second place a write can
 * be described.
 *
 * Four slots were added for it, and none of them weakens the rule above:
 *
 * - **{@link consequence} and {@link caution}** are both **server** strings, and they are two
 *   props rather than two entries in {@link disclosures} because they are not `doesNotReach`
 *   lines. A disclosure says what a write will not *touch*; these say what it does achieve and
 *   what it will not have *finished*. `RevokeFlow.dc.html` draws them as two blocks, the
 *   second warmer than the first, which is what the treatment below reproduces. The caution in
 *   particular carries ADR 0021 decision 9's residual — *"it may keep working for up to eight
 *   hours"* — a measured property of the Stytch project that a browser bundle composing its
 *   own copy would go on asserting after the project changed.
 * - **{@link confirmLabel} / {@link cancelLabel}** are the two strings this component already
 *   owned. #293's `RevokeFlow.dc.html` labels them *Cut it off* and *Leave it*, and a control
 *   label is exactly the kind of copy the disband gate's own note already licenses: an
 *   instruction for a control, saying nothing about the write. The defaults are unchanged, so
 *   every existing caller reads identically.
 */
/**
 * A value that has to be typed back before the confirm is live — see the header.
 *
 * `expected` is compared after trimming and nothing else: a family's name may hold anything,
 * and case-folding *"the riveras"* into a match would make the ceremony weaker than the one
 * drawn without saying so.
 */
export interface ConfirmGate {
  readonly label: string;
  readonly expected: string;
}

/**
 * The second factor, once the server has asked for one (#307) — see the header.
 *
 * Present means *the write was refused because this session has not authenticated recently,
 * and a code has been sent*. The panel never decides to show this; it is told to, by a
 * `step_up_required` refusal, and a family who has authenticated in the last few minutes
 * never sees it at all.
 */
export interface ConfirmChallenge {
  /**
   * Where the code went, as the server said — `POST /app/me/step-up` resolves the address from
   * the session, so the panel neither knows nor composes it.
   */
  readonly sentTo: string;
  /** Another send is in flight, so *Send another* is inert and says so. */
  readonly sending: boolean;
  readonly onResend: () => void;
  /**
   * The primary button's label on **this** step, when the caller wants one of its own —
   * `DisbandFlow.dc.html` labels it *Confirm deletion* where the step before says *Delete the
   * family*. A control label, on the licence {@link confirmLabel} already runs on. Falls back
   * to {@link confirmLabel}, so a covered write the canvas does not draw reads consistently
   * rather than inventing a sentence.
   */
  readonly confirmLabel?: string | undefined;
}

export function Confirm({
  message,
  disclosures = [],
  consequence,
  caution,
  gate,
  challenge,
  pending,
  error,
  confirmLabel = "Yes, go ahead",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
}: {
  /** The tool's own family-facing sentence. Rendered verbatim, never edited or summarised. */
  message: string;
  /** The tool's own `doesNotReach` lines, if it has any. Server strings, in order. */
  disclosures?: readonly string[];
  /** A server sentence about what this write **does** achieve — see the header. */
  consequence?: string | undefined;
  /**
   * A server sentence about what this write will **not have finished** when it returns — see
   * the header. #116's residual window is the only one today.
   */
  caution?: string | undefined;
  gate?: ConfirmGate | undefined;
  /** The second factor, when the server has asked for one. See {@link ConfirmChallenge}. */
  challenge?: ConfirmChallenge | undefined;
  pending: boolean;
  /** A generic failure line — never a vendor string or a code (the login app's rule, #57). */
  error: string | null;
  /** Defaults to *Yes, go ahead*. A control label, never a description of the write. */
  confirmLabel?: string | undefined;
  /** Defaults to *Cancel*. */
  cancelLabel?: string | undefined;
  /**
   * The family's yes. Handed the typed code on the challenge step, and nothing on every other
   * — so an existing caller is unchanged and a caller that shows a challenge cannot forget to
   * pass the one thing that satisfies it.
   */
  onConfirm: (stepUpCode?: string) => void;
  onCancel: () => void;
}) {
  const labelId = useId();
  const gateId = useId();
  const codeId = useId();
  const [typed, setTyped] = useState("");
  const [code, setCode] = useState("");
  /**
   * **The challenge replaces the gate rather than sitting beside it.** The typed name has
   * already been given on the step before — the proposal it unlocked is what is being
   * confirmed — and asking for it again next to a code would read as though the first answer
   * had been thrown away.
   */
  const unlocked =
    challenge !== undefined
      ? code.trim().length > 0
      : gate === undefined || typed.trim() === gate.expected;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  /**
   * The keyboard contract, and **each half is a separate effect on purpose**.
   *
   * Focus lands on **Cancel** rather than on the confirm — every write this dialog stands in
   * front of is one the family cannot undo, so the key easiest to hit by accident must be the
   * one that does nothing. That happens **once, when the dialog appears**. It used to share an
   * effect with the key handler, whose dependency is `onCancel` — an inline arrow in
   * `Memory.tsx`, and therefore a new identity on every parent render. So pressing *Yes* (which
   * flips `pending`) re-ran the effect and yanked focus back onto the now-disabled Cancel,
   * mid-interaction, immediately after a keyboard user committed to an irreversible write.
   */
  useEffect(() => {
    // Remember what opened this so Cancel can hand focus back. Captured at mount, because by
    // unmount the active element is a button inside this dialog.
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelRef.current?.focus();
    return () => {
      // **Only if it is still there.** After an applied write the list is re-read and the row
      // that opened this is a different node, so focusing the old one is impossible and
      // focusing something else would move a keyboard somewhere nobody asked for. On cancel
      // nothing has changed, the control still exists, and this is the case the review
      // separated out: the trigger-is-gone reasoning covers success and not cancellation.
      const previous = opener.current;
      if (previous !== null && document.contains(previous)) previous.focus();
    };
  }, []);

  /**
   * **Escape is ignored once the write is in flight**, which is the half that was wrong.
   *
   * Cancel is disabled while `pending`, but this listener was not — so after *Yes, go ahead* a
   * person could press Escape, watch the dialog vanish, and conclude they had cancelled. The
   * request is not aborted and the delete still commits: a false outcome on the one write with
   * no undo. `pending` now gates the handler, so the dialog stays put until the answer lands.
   */
  /**
   * **Focus moves to the code when the challenge arrives**, and this is a second effect rather
   * than a change to the one above for the reason that one was split: the rule up there is
   * *land on the key that does nothing, once, when a dialog appears in front of an irreversible
   * write*, and it is right. This step is not that moment — the write has already been agreed
   * to, the dialog is already open and mounted (so `autoFocus` would never fire), and the only
   * thing left to do is type six characters that arrived in an email. Leaving focus on Cancel
   * here would make a keyboard user tab into the one field on the screen.
   */
  const challenged = challenge !== undefined;
  useEffect(() => {
    if (challenged) codeRef.current?.focus();
  }, [challenged]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || pending) return;
      onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, pending]);

  /**
   * **Tab is kept inside the dialog**, because `aria-modal="true"` is otherwise a claim nothing
   * makes true.
   *
   * The concrete failure this closes: focus starts on Cancel, which is the *first* control, so
   * **Shift+Tab** — the natural "go back" — left the dialog and landed on the last fact's
   * *Take this back* underneath the scrim, which is enabled while a proposal is merely open.
   * Activating it replaced the pending proposal with a different one, and *Yes* then deleted a
   * fact the family never opened the dialog about.
   *
   * A cycle rather than `inert` on the shell: `inert` needs a handle on a root this component
   * does not own, and the native `<dialog>` top layer would change how the whole panel is
   * mounted. Cycling is the part that fixes the reachable bug, in the component that has the
   * bug. Screens outside the dialog are additionally made unreachable by `Memory.tsx`, which
   * disables every list action while a confirmation is open.
   */
  useEffect(() => {
    const onTab = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const root = dialogRef.current;
      if (root === null) return;
      // Inputs count: the disband gate puts a text field inside the dialog, and a cycle that
      // only knew about buttons would let Shift+Tab off the first button and out of the modal
      // — the exact escape the comment above is about, one control type wider.
      const focusable = [
        ...root.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled])"),
      ];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        // Every control is disabled — mid-write. Keep focus in the dialog rather than letting
        // Tab escape to the page behind it.
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !root.contains(active))) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && (active === last || !root.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onTab);
    return () => window.removeEventListener("keydown", onTab);
  }, []);

  return (
    <div className="scrim scrim--dialog">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        ref={dialogRef}
      >
        {challenge === undefined ? (
          <>
            <p className="dialog__message" id={labelId}>
              {message}
            </p>
            {disclosures.length === 0 ? null : (
              <ul className="dialog__list">
                {disclosures.map((line) => (
                  <li className="dialog__list-item" key={line}>
                    {line}
                  </li>
                ))}
              </ul>
            )}
            {consequence === undefined ? null : <p className="dialog__note">{consequence}</p>}
            {caution === undefined ? null : <p className="dialog__caution">{caution}</p>}
            {gate === undefined ? null : (
              <div className="field dialog__gate">
                <label className="field__label" htmlFor={gateId}>
                  {gate.label}
                </label>
                <input
                  className="field__input"
                  id={gateId}
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  disabled={pending}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {/* The dialog's accessible name on this step, and an `h2` like `TakeACopy`'s. A
                heading is allowed here where it is not above a write's message, for
                `dialog__title`'s reason: it describes the control, not the write. */}
            <h2 className="dialog__title" id={labelId}>
              Enter the code
            </h2>
            <p className="dialog__message">Sent to {challenge.sentTo}.</p>
            <div className="field dialog__gate">
              <label className="field__label" htmlFor={codeId}>
                Code
              </label>
              <input
                className="field__input field__input--code"
                id={codeId}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                disabled={pending}
                // `one-time-code` is what lets a phone offer the code straight from the
                // message, which is the whole difference between this taking three seconds and
                // taking a trip to another app.
                autoComplete="one-time-code"
                inputMode="numeric"
                autoCorrect="off"
                spellCheck={false}
                ref={codeRef}
              />
            </div>
          </>
        )}
        {error === null ? null : (
          <p className="dialog__error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog__actions">
          <button
            className="cta cta--quiet"
            type="button"
            onClick={onCancel}
            disabled={pending}
            ref={cancelRef}
          >
            {cancelLabel}
          </button>
          <button
            className="cta"
            type="button"
            onClick={() => onConfirm(challenge === undefined ? undefined : code.trim())}
            disabled={pending || !unlocked}
          >
            {pending ? "Working…" : (challenge?.confirmLabel ?? confirmLabel)}
          </button>
        </div>
        {challenge === undefined ? null : (
          <p className="dialog__resend">
            Didn&rsquo;t arrive?{" "}
            <button
              className="linklike"
              type="button"
              onClick={challenge.onResend}
              disabled={pending || challenge.sending}
            >
              {challenge.sending ? "Sending…" : "Send another"}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
